import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from '../security/auth.service';
import { SessionStorageService } from '../services/session-storage.service';
import { ToolbarService } from '../services/toolbar.service';

const TOKEN_HEADER_KEY = 'Authorization';
const SESSION_HEADER_KEY = 'X-Cargomate-Session-Id';
const SESSION_CLIENT_HEADER_KEY = 'X-Cargomate-Session-Client';
const SESSION_CLIENT = 'ENTERPRISE_PORTAL';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  // Exclude static assets and specific URLs from interception
  if (isExcludedFromInterception(req.url)) {
    return next(req);
  }

  // Inject services inside the function
  const sessionStorageService = inject(SessionStorageService);
  const authService = inject(AuthService);
  const injector = inject(Injector);
  const toolbarService = inject(ToolbarService);

  const navigateToLogin = (queryParams?: Record<string, string>) => {
    try {
      const router = injector.get(Router);
      if (queryParams) {
        router.navigate(['/login'], { queryParams });
      } else {
        router.navigate(['/login']);
      }
    } catch {
      window.location.href = '/login';
    }
  };

  const savedUser = sessionStorageService.getUser();
  const token = sessionStorageService.getAccessToken();

  const proceedWithRequest = (accessToken: string | null): Observable<any> => {
    const authReq = buildAuthenticatedRequest(req, sessionStorageService, accessToken);

    return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // If the error is not a 401, just pass it along
      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (isSessionReplacementError(error)) {
        clearAuthenticatedShell(sessionStorageService, toolbarService, authService);
        navigateToLogin({ session: 'replaced' });
        return throwError(() => error);
      }

      // If it is a 401, attempt to refresh the token when we still have a session
      if (!accessToken || !authService.hasCurrentUser()) {
        clearAuthenticatedShell(sessionStorageService, toolbarService, authService);
        navigateToLogin();
        return throwError(() => error);
      }

      return authService.getCurrentUserAccessToken().pipe(
        switchMap((newAccessToken: string) => {
          sessionStorageService.saveAccessToken(newAccessToken, new Date().getTime());
          const newAuthReq = buildAuthenticatedRequest(req, sessionStorageService, newAccessToken);
          return next(newAuthReq);
        }),
        catchError((refreshError) => {
          // If the refresh fails, logout and redirect to login
          console.error('Token refresh failed:', refreshError);
          clearAuthenticatedShell(sessionStorageService, toolbarService, authService);
          navigateToLogin();
          return throwError(() => refreshError);
        })
      );
    })
  );
  };

  if (token) {
    return proceedWithRequest(token);
  }

  if (!savedUser) {
    return proceedWithRequest(null);
  }

  return authService.getCurrentUserAccessToken().pipe(
    switchMap((freshAccessToken: string) => {
      sessionStorageService.saveAccessToken(freshAccessToken, new Date().getTime());
      return proceedWithRequest(freshAccessToken);
    }),
    catchError((refreshError) => {
      console.error('Initial token bootstrap failed:', refreshError);
      clearAuthenticatedShell(sessionStorageService, toolbarService, authService);
      navigateToLogin();
      return throwError(() => refreshError);
    })
  );
};

function buildAuthenticatedRequest(req: Parameters<HttpInterceptorFn>[0], sessionStorageService: SessionStorageService, accessToken: string | null) {
  if (!accessToken) {
    return req;
  }

  const browserSessionId = sessionStorageService.ensureBrowserSessionId();
  const headers = req.headers
    .set(TOKEN_HEADER_KEY, `Bearer ${accessToken}`)
    .set(SESSION_HEADER_KEY, browserSessionId)
    .set(SESSION_CLIENT_HEADER_KEY, SESSION_CLIENT);

  return req.clone({ headers });
}

function isSessionReplacementError(error: HttpErrorResponse): boolean {
  const body = error.error;
  if (body && typeof body === 'object' && body.code === 'SESSION_REPLACED') {
    return true;
  }
  if (typeof body === 'string') {
    return body.includes('SESSION_REPLACED');
  }
  return false;
}

function isExcludedFromInterception(url: string): boolean {
  return url.includes('/assets/')
    || url.includes('assets/')
    || url.includes('traccar')
    || url.includes('8082')
    || isPublicApiRequest(url);
}

function isPublicApiRequest(url: string): boolean {
  return url.includes('/api/public/')
    || url.includes('public/transport/order-release-tracking')
    || url.includes('/api/posm/closure/client-tracking/')
    || url.includes('posm/closure/client-tracking/');
}

function clearAuthenticatedShell(
  sessionStorageService: SessionStorageService,
  toolbarService: ToolbarService,
  authService: AuthService
): void {
  sessionStorageService.signOut();
  toolbarService.updateCustomer(null);
  toolbarService.updateEnterpriseFeatures(null);
  toolbarService.updateLoggedInStatus(false);
  authService.logout().subscribe({ error: () => undefined });
}
